import {
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt';
import { differenceInCalendarMonths, format, isAfter, isBefore } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CONSTANTS } from '../../../../core/config/constants';
import { MARKERS } from '../CalendarPicker';
import {
  CalendarPickerDateRange,
  CalendarPickerDefinedRange,
  CalendarPickerNavigationAction,
  CalendarPickerSetter,
} from '../calendarPicker.types';
import CalendarPickerDefinedRanges from './CalendarPickerDefinedRanges';
import Month from './CalendarPickerMonth';

interface Props {
  dateRange: CalendarPickerDateRange;
  ranges: CalendarPickerDefinedRange[];
  minDate: Date;
  maxDate: Date;
  showDefinedRanges: boolean;
  isRange: boolean;
  firstMonth: Date;
  secondMonth: Date;
  setFirstMonth: CalendarPickerSetter<Date>;
  setSecondMonth: CalendarPickerSetter<Date>;
  setDateRange: CalendarPickerSetter<CalendarPickerDateRange>;
  helpers: {
    inHoverRange: (day: Date) => boolean;
  };
  handlers: {
    onDayClick: (day: Date) => void;
    onDayHover: (day: Date) => void;
    onMonthNavigate: (marker: symbol, action: CalendarPickerNavigationAction) => void;
  };
}

const CalendarPickerMenu: React.FC<Props> = ({
  ranges,
  dateRange,
  minDate,
  maxDate,
  firstMonth,
  setFirstMonth,
  secondMonth,
  setSecondMonth,
  setDateRange,
  helpers,
  handlers,
  showDefinedRanges,
  isRange,
}) => {
  const classes = useStyles();
  const { startDate, endDate } = dateRange;
  const { t } = useTranslation();
  const canNavigateCloser = isRange
    ? differenceInCalendarMonths(secondMonth, firstMonth) >= 2
    : differenceInCalendarMonths(maxDate, firstMonth) >= 1;
  const canNavigateMin =
    isAfter(firstMonth, minDate) && differenceInCalendarMonths(firstMonth, minDate) >= 1;
  const canNavigateMax =
    isBefore(secondMonth, maxDate) && differenceInCalendarMonths(maxDate, secondMonth) >= 1;
  const commonProps = { dateRange, minDate, maxDate, helpers, handlers, isRange };

  return (
    <Paper elevation={5} square>
      <Grid container direction="row" wrap="nowrap">
        <Grid>
          {isRange && (
            <>
              <Grid container className={classes.header} alignItems="center">
                <Grid item className={classes.headerItem}>
                  <Typography variant="subtitle1">
                    {startDate
                      ? format(startDate, CONSTANTS.DATE_FORMATS.UI)
                      : t('app:datepicker:startDate')}
                  </Typography>
                </Grid>
                <Grid item className={classes.headerItem}>
                  <ArrowRightAlt color="action" />
                </Grid>
                <Grid item className={classes.headerItem}>
                  <Typography variant="subtitle1">
                    {endDate
                      ? format(endDate, CONSTANTS.DATE_FORMATS.UI)
                      : t('app:datepicker:endDate')}
                  </Typography>
                </Grid>
              </Grid>
              <Divider />
            </>
          )}
          <Grid container direction="row" justify="center" wrap="nowrap">
            <Month
              {...commonProps}
              value={firstMonth}
              setValue={setFirstMonth}
              navState={[canNavigateMin, canNavigateCloser]}
              marker={MARKERS.FIRST_MONTH}
            />
            {isRange && (
              <>
                <div className={classes.divider} />
                <Month
                  {...commonProps}
                  value={secondMonth}
                  setValue={setSecondMonth}
                  navState={[canNavigateCloser, canNavigateMax]}
                  marker={MARKERS.SECOND_MONTH}
                />
              </>
            )}
          </Grid>
        </Grid>
        {showDefinedRanges && (
          <>
            <div className={classes.divider} />
            <Grid>
              <CalendarPickerDefinedRanges
                selectedRange={dateRange}
                ranges={ranges}
                setRange={setDateRange}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      padding: '10px 70px',
    },
    headerItem: {
      flex: 1,
      textAlign: 'center',
    },
    divider: {
      borderLeft: `1px solid ${theme.palette.action.hover}`,
      marginBottom: 20,
    },
  }),
);

export default CalendarPickerMenu;
